import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { Link } from '@tanstack/react-router';
import { usePrepTasks, type PrepTask } from '@/lib/nutrition';

/**
 * Vista "Preparaciones" del módulo Nutrición (M2.34.B).
 *
 * Lista cronológica de tareas de preparación (`prepMin >= 30`) calculadas por
 * el backend. Cada tarea sugiere ejecutar la preparación 1h antes de la
 * comida; el usuario marca "hecho" para llevar la cuenta visual del día.
 *
 * Decisiones:
 * - "Hecho" se persiste en localStorage por semana
 *   (`tri-mentor:prep:done:{weekStartISO}`). NO viaja al backend — es estado
 *   local del día y la lista se regenera cada vez que cambia el plan.
 * - Las tareas pasadas (>2h) ya las filtra el backend. La UI sólo agrupa por
 *   fecha local del usuario.
 * - El enlace al recipe usa `<Link to="/nutricion" search={{ tab: 'recetas', recipeId }}>`
 *   para que el tab de Recetas lo abra (la deep-link de receta concreta vive
 *   en M2.34.A; si no está disponible, basta con abrir el tab).
 */
export function PrepTasksView({
  weekStartISO,
}: {
  weekStartISO: string;
}): ReactElement {
  const query = usePrepTasks(weekStartISO);

  return (
    <section aria-label="Preparaciones de la semana" className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">
          Preparaciones
        </h2>
        <p className="text-sm text-slate-600">
          Recetas largas (más de 30 min de prep) sugeridas 1 hora antes de la
          comida.
        </p>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando preparaciones…
        </p>
      )}

      {query.isError && (
        <p
          role="alert"
          className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudieron cargar las preparaciones.{' '}
          {query.error?.message ?? ''}
        </p>
      )}

      {query.data && <PrepTasksBody tasks={query.data.tasks} weekStartISO={weekStartISO} />}
    </section>
  );
}

// ---------------------------------------------------------------------------

function PrepTasksBody({
  tasks,
  weekStartISO,
}: {
  tasks: PrepTask[];
  weekStartISO: string;
}): ReactElement {
  const { done, toggle } = useDoneState(weekStartISO, tasks);

  if (tasks.length === 0) {
    return (
      <section
        aria-label="Sin preparaciones"
        className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
      >
        <p className="text-sm text-slate-700">
          No hay preparaciones &gt;30 min esta semana.
        </p>
      </section>
    );
  }

  const grouped = groupByDay(tasks);

  return (
    <div className="space-y-4">
      {grouped.map((g) => (
        <section
          key={g.dayKey}
          aria-label={`Día ${g.dayLabel}`}
          className="rounded-lg border border-slate-200 bg-white shadow-sm"
        >
          <header className="border-b border-slate-200 px-4 py-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              {g.dayLabel}
            </h3>
          </header>
          <ul className="divide-y divide-slate-100">
            {g.tasks.map((t) => {
              const isDone = Boolean(done[t.id]);
              return (
                <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isDone}
                    onChange={(e) => toggle(t.id, e.target.checked)}
                    aria-label={`Hecho: ${t.title}`}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={
                        isDone
                          ? 'text-sm text-slate-400 line-through'
                          : 'text-sm font-medium text-slate-900'
                      }
                    >
                      {t.title}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatTime(t.suggestedDatetimeISO)} ·{' '}
                      {t.prepMinutes} min prep
                    </p>
                  </div>
                  <Link
                    to="/nutricion"
                    search={{ tab: 'recetas', recipeId: t.recipeId }}
                    className="text-xs font-medium text-teal-700 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
                  >
                    Ver receta
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook estado "hecho"
// ---------------------------------------------------------------------------

function useDoneState(
  weekStartISO: string,
  tasks: PrepTask[],
): { done: Record<string, boolean>; toggle: (id: string, value: boolean) => void } {
  const storageKey = `tri-mentor:prep:done:${weekStartISO}`;
  const [done, setDone] = useState<Record<string, boolean>>(() =>
    loadDone(storageKey),
  );

  useEffect(() => {
    const loaded = loadDone(storageKey);
    const taskIds = new Set(tasks.map((t) => t.id));
    const filtered: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(loaded)) {
      if (taskIds.has(k)) filtered[k] = v;
    }
    setDone(filtered);
  }, [storageKey, tasks]);

  const toggle = useCallback(
    (id: string, value: boolean) => {
      const next = { ...done, [id]: value };
      setDone(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* swallow */
      }
    },
    [done, storageKey],
  );

  return { done, toggle };
}

function loadDone(storageKey: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'boolean') out[k] = v;
      }
      return out;
    }
  } catch {
    /* swallow */
  }
  return {};
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByDay(
  tasks: PrepTask[],
): Array<{ dayKey: string; dayLabel: string; tasks: PrepTask[] }> {
  // Las tareas vienen ya ordenadas cronológicamente desde el backend.
  // Agrupamos por fecha local (YYYY-MM-DD del navegador).
  const map = new Map<string, PrepTask[]>();
  for (const t of tasks) {
    const d = new Date(t.suggestedDatetimeISO);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const arr = map.get(key) ?? [];
    arr.push(t);
    map.set(key, arr);
  }
  const keys = [...map.keys()].sort();
  return keys.map((k) => ({
    dayKey: k,
    dayLabel: formatDayLabel(k),
    tasks: map.get(k)!,
  }));
}

function formatDayLabel(yyyyMmDd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd);
  if (!m) return yyyyMmDd;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  const fmt = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
  return fmt.format(date);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt.format(d);
}
